// Side panel that lists the FEEL variables visible in the current editing
// context. Plain DOM, no framework. Consumers feed it a scope array computed
// by `computeScope()`; the panel groups by kind and renders typed chips.
//
// Phase 1: read-only. Drag/drop and click-to-insert will arrive in Phase 2.

import './styles.css';
import { displayType } from './computeScope';
import { FUNCTION_CATALOG, FUNCTION_CATEGORIES } from '../feel/index';
import { FEEL_TYPES } from '../widgets/feelTypes';

const GROUP_ORDER = ['param', 'input', 'decision', 'function', 'self'];
const GROUP_LABELS = {
  param: 'Parámetros',
  input: 'Inputs requeridos',
  decision: 'Decisiones requeridas',
  function: 'Funciones (BKM)',
  self: 'Output de esta decisión',
};

const BUILTINS_BY_CATEGORY = (() => {
  const map = new Map();
  FUNCTION_CATEGORIES.forEach((c) => map.set(c.id, []));
  FUNCTION_CATALOG.forEach((fn) => {
    if (!map.has(fn.category)) map.set(fn.category, []);
    map.get(fn.category).push(fn);
  });
  return map;
})();

export class ScopePanel {
  constructor(container, { onSetTypeRef, onPromoteOrphan } = {}) {
    if (!container) throw new Error('[ScopePanel] container required');
    this._container = container;
    this._scope = [];
    this._orphans = [];
    this._builtinsOpen = false;
    this._onSetTypeRef = typeof onSetTypeRef === 'function' ? onSetTypeRef : null;
    this._onPromoteOrphan = typeof onPromoteOrphan === 'function' ? onPromoteOrphan : null;
    this._container.classList.add('epx-dmn-scope');
    this._render();
  }

  setScope(scope) {
    this._scope = Array.isArray(scope) ? scope : [];
    this._render();
  }

  setOrphans(orphans) {
    this._orphans = Array.isArray(orphans) ? orphans : [];
    this._render();
  }

  destroy() {
    this._container.innerHTML = '';
    this._container.classList.remove('epx-dmn-scope');
  }

  _render() {
    this._container.innerHTML = '';

    const header = document.createElement('div');
    header.className = 'epx-dmn-scope__header';
    header.textContent = 'Variables en scope';
    this._container.appendChild(header);

    this._container.appendChild(this._renderOrphansSection());

    if (!this._scope.length) {
      const empty = document.createElement('div');
      empty.className = 'epx-dmn-scope__empty';
      empty.textContent = 'No hay variables visibles aquí todavía. Conecta inputs o decisiones al dmn.';
      this._container.appendChild(empty);
    } else {
      const groups = new Map();
      this._scope.forEach((v) => {
        if (!groups.has(v.kind)) groups.set(v.kind, []);
        groups.get(v.kind).push(v);
      });

      GROUP_ORDER.forEach((kind) => {
        const list = groups.get(kind);
        if (!list || !list.length) return;
        const section = document.createElement('div');
        section.className = 'epx-dmn-scope__section';

        const h = document.createElement('div');
        h.className = `epx-dmn-scope__section-head epx-dmn-scope__section-head--${kind}`;
        h.textContent = GROUP_LABELS[kind] || kind;
        section.appendChild(h);

        list.forEach((v) => section.appendChild(this._renderChip(v)));
        this._container.appendChild(section);
      });
    }

    this._container.appendChild(this._renderBuiltins());
  }

  _renderOrphansSection() {
    const section = document.createElement('div');
    section.className = 'epx-dmn-scope__section epx-dmn-scope__section--orphans';

    const head = document.createElement('div');
    head.className = 'epx-dmn-scope__section-head epx-dmn-scope__section-head--orphan';
    const label = document.createElement('span');
    label.textContent = 'Variables no traqueadas';
    head.appendChild(label);
    const count = document.createElement('span');
    count.className = 'epx-dmn-scope__orphan-count';
    count.textContent = String(this._orphans.length);
    head.appendChild(count);
    section.appendChild(head);

    if (!this._orphans.length) {
      const empty = document.createElement('div');
      empty.className = 'epx-dmn-scope__orphan-empty';
      empty.textContent = 'Sin referencias sueltas en la expresión.';
      section.appendChild(empty);
      return section;
    }

    this._orphans.forEach((o) => section.appendChild(this._renderOrphanChip(o)));
    return section;
  }

  _renderOrphanChip(o) {
    const chip = document.createElement('div');
    chip.className = 'epx-dmn-scope__chip epx-dmn-scope__chip--orphan';
    chip.title = `${o.name} no está conectado. Click ＋ para crearlo.`;

    const nameRow = document.createElement('div');
    nameRow.className = 'epx-dmn-scope__chip-row';

    const name = document.createElement('span');
    name.className = 'epx-dmn-scope__chip-name';
    name.textContent = o.name;
    nameRow.appendChild(name);

    const badge = document.createElement('span');
    badge.className = 'epx-dmn-scope__chip-type';
    badge.textContent = displayType(o.typeRef);
    nameRow.appendChild(badge);

    if (this._onPromoteOrphan) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'epx-dmn-scope__orphan-promote';
      btn.textContent = '＋';
      btn.title = 'Crear este input/parámetro y conectarlo';
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._onPromoteOrphan(o.name);
      });
      nameRow.appendChild(btn);
    }

    chip.appendChild(nameRow);
    return chip;
  }

  _renderBuiltins() {
    const wrap = document.createElement('div');
    wrap.className = 'epx-dmn-scope__section epx-dmn-scope__section--builtins';

    const head = document.createElement('button');
    head.type = 'button';
    head.className = 'epx-dmn-scope__builtins-head';
    head.setAttribute('aria-expanded', String(this._builtinsOpen));
    const arrow = document.createElement('span');
    arrow.className = 'epx-dmn-scope__builtins-arrow';
    arrow.textContent = this._builtinsOpen ? '▾' : '▸';
    const label = document.createElement('span');
    label.textContent = 'Funciones FEEL';
    const count = document.createElement('span');
    count.className = 'epx-dmn-scope__builtins-count';
    count.textContent = String(FUNCTION_CATALOG.length);
    head.append(arrow, label, count);
    head.addEventListener('click', () => {
      this._builtinsOpen = !this._builtinsOpen;
      this._render();
    });
    wrap.appendChild(head);

    if (!this._builtinsOpen) return wrap;

    FUNCTION_CATEGORIES.forEach((cat) => {
      const list = BUILTINS_BY_CATEGORY.get(cat.id) || [];
      if (!list.length) return;
      const catEl = document.createElement('div');
      catEl.className = 'epx-dmn-scope__builtins-cat';
      const catHead = document.createElement('div');
      catHead.className = 'epx-dmn-scope__builtins-cat-head';
      catHead.textContent = cat.label;
      catEl.appendChild(catHead);
      list.forEach((fn) => catEl.appendChild(this._renderBuiltinChip(fn)));
      wrap.appendChild(catEl);
    });

    return wrap;
  }

  _renderBuiltinChip(fn) {
    const chip = document.createElement('div');
    chip.className = 'epx-dmn-scope__builtin epx-dmn-scope__chip--clickable';
    const name = document.createElement('span');
    name.className = 'epx-dmn-scope__builtin-name';
    name.textContent = fn.name;
    const sig = document.createElement('span');
    sig.className = 'epx-dmn-scope__builtin-sig';
    sig.textContent = `(${(fn.params || []).join(', ')})`;
    chip.append(name, sig);
    if (fn.desc) {
      const desc = document.createElement('div');
      desc.className = 'epx-dmn-scope__builtin-desc';
      desc.textContent = fn.desc;
      chip.appendChild(desc);
    }
    chip.title = `${fn.name}(${(fn.params || []).join(', ')})${fn.desc ? '\n' + fn.desc : ''}\nClick para insertar.`;
    chip.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      const snippet = `${fn.name}(${(fn.params || []).join(', ')})`;
      if (insertIntoFocusedEditable(snippet)) {
        e.preventDefault();
      }
    });
    return chip;
  }

  _renderChip(v) {
    const chip = document.createElement('div');
    chip.className = `epx-dmn-scope__chip epx-dmn-scope__chip--${v.kind}`;

    const nameRow = document.createElement('div');
    nameRow.className = 'epx-dmn-scope__chip-row';

    const name = document.createElement('span');
    name.className = 'epx-dmn-scope__chip-name';
    name.textContent = v.name || '(sin nombre)';
    nameRow.appendChild(name);

    if (v.kind === 'function' && v.signature && v.signature.length) {
      const parens = document.createElement('span');
      parens.className = 'epx-dmn-scope__chip-parens';
      const sig = v.signature.map((p) => p.name || '?').join(', ');
      parens.textContent = `(${sig})`;
      nameRow.appendChild(parens);
    }

    const editable = v.kind === 'input' && v.sourceId && this._onSetTypeRef;
    let badge;
    if (editable) {
      badge = document.createElement('select');
      badge.className = 'epx-dmn-scope__chip-type epx-dmn-scope__chip-type--editable';
      const current = displayType(v.typeRef);
      let matched = false;
      FEEL_TYPES.forEach((t) => {
        const opt = document.createElement('option');
        opt.value = t;
        opt.textContent = t;
        if (t === current) { opt.selected = true; matched = true; }
        badge.appendChild(opt);
      });
      if (!matched) {
        const opt = document.createElement('option');
        opt.value = current;
        opt.textContent = current;
        opt.selected = true;
        badge.insertBefore(opt, badge.firstChild);
      }
      const stop = (e) => e.stopPropagation();
      badge.addEventListener('mousedown', stop);
      badge.addEventListener('click', stop);
      badge.addEventListener('change', (e) => {
        e.stopPropagation();
        this._onSetTypeRef(v.sourceId, badge.value || null);
      });
    } else {
      badge = document.createElement('span');
      badge.className = 'epx-dmn-scope__chip-type';
      badge.textContent = displayType(v.typeRef);
    }
    nameRow.appendChild(badge);

    chip.appendChild(nameRow);

    const baseTitle = v.kind === 'function' && v.signature && v.signature.length
      ? `${v.name}(${v.signature
          .map((p) => `${p.name || '?'}: ${displayType(p.typeRef)}`)
          .join(', ')}) → ${displayType(v.typeRef)}`
      : `${v.name} : ${displayType(v.typeRef)}${v.sourceName ? ' — ' + v.sourceName : ''}`;
    chip.title = v.kind === 'self'
      ? baseTitle
      : `${baseTitle}\nClick para insertar en la expresión con foco.`;

    if (v.sourceName && v.sourceName !== v.name && v.kind !== 'self') {
      const sub = document.createElement('div');
      sub.className = 'epx-dmn-scope__chip-source';
      sub.textContent = `← ${v.sourceName}`;
      chip.appendChild(sub);
    }

    if (v.kind !== 'self') {
      chip.classList.add('epx-dmn-scope__chip--clickable');
      chip.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        const snippet = this._snippetFor(v);
        if (!snippet) return;
        if (insertIntoFocusedEditable(snippet)) {
          e.preventDefault();
        }
      });
    }

    return chip;
  }

  _snippetFor(v) {
    if (!v || !v.name) return '';
    if (v.kind === 'function' && v.signature && v.signature.length) {
      return `${v.name}(${v.signature.map((p) => p.name || '').join(', ')})`;
    }
    return v.name;
  }
}

// Insert `text` at the caret of the document's currently focused editable
// (contenteditable span or input/textarea). Returns true when an insertion
// happened so the caller can swallow the click.
function insertIntoFocusedEditable(text) {
  const el = document.activeElement;
  if (!el) return false;

  if (el.isContentEditable) {
    try {
      const ok = document.execCommand('insertText', false, text);
      if (ok) {
        el.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: text }));
        return true;
      }
    } catch { /* fall through */ }
    const sel = window.getSelection();
    if (sel && sel.rangeCount) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(text));
      range.collapse(false);
      el.dispatchEvent(new InputEvent('input', { bubbles: true }));
      return true;
    }
    return false;
  }

  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    el.value = el.value.slice(0, start) + text + el.value.slice(end);
    const pos = start + text.length;
    el.setSelectionRange(pos, pos);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  return false;
}
